import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';

const ROLES = [
  { value: 'primary_owner', label: 'Primary Owner', level: 0, description: 'Full ownership, cannot be changed' },
  { value: 'owner', label: 'Owner', level: 1, description: 'Full access, can manage all aspects of the company' },
  { value: 'executive', label: 'Executive', level: 2, description: 'High-level access, can manage most operations' },
  { value: 'general_manager', label: 'General Manager', level: 3, description: 'Manages day-to-day operations' },
  { value: 'department_head', label: 'Department Head', level: 4, description: 'Manages a specific department' },
  { value: 'manager', label: 'Manager', level: 5, description: 'Manages a team within a department' },
  { value: 'supervisor', label: 'Supervisor', level: 6, description: 'Oversees daily work of staff' },
  { value: 'staff', label: 'Staff', level: 7, description: 'Regular employee with basic access' },
  { value: 'contractor', label: 'Contractor', level: 7, description: 'External contractor with limited access' }
];

const ChangeRoleModal = ({ isOpen, onClose, member, onSave, currentUserRole }) => {
  const [selectedRole, setSelectedRole] = useState(member?.role || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const currentRoleLevel = ROLES.find(r => r.value === currentUserRole)?.level ?? 99;

  // Filter roles that the current user can assign (only roles below their level)
  const availableRoles = ROLES.filter(role => {
    // Can't assign primary_owner
    if (role.value === 'primary_owner') return false;
    // Can only assign roles below your level
    return role.level > currentRoleLevel;
  });

  const formatRole = (role) => {
    return role?.split('_')?.map(word =>
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || 'N/A';
  };

  const handleSave = async () => {
    if (!selectedRole || selectedRole === member?.role) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError('');
      await onSave(member.id, selectedRole);
      onClose();
    } catch (err) {
      console.error('Error changing role:', err);
      setError(err.message || 'Failed to change role');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'primary_owner': 'bg-purple-500 bg-opacity-20 text-purple-400 border-purple-500',
      'owner': 'bg-purple-500 bg-opacity-20 text-purple-400 border-purple-500',
      'executive': 'bg-blue-500 bg-opacity-20 text-blue-400 border-blue-500',
      'general_manager': 'bg-blue-500 bg-opacity-20 text-blue-400 border-blue-500',
      'department_head': 'bg-green-500 bg-opacity-20 text-green-400 border-green-500',
      'manager': 'bg-green-500 bg-opacity-20 text-green-400 border-green-500',
      'supervisor': 'bg-yellow-500 bg-opacity-20 text-yellow-400 border-yellow-500',
      'staff': 'bg-gray-500 bg-opacity-20 text-gray-400 border-gray-500',
      'contractor': 'bg-orange-500 bg-opacity-20 text-orange-400 border-orange-500'
    };
    return colors[role] || 'bg-gray-500 bg-opacity-20 text-gray-400 border-gray-500';
  };

  const hasChanges = selectedRole && selectedRole !== member?.role;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Change Role</h2>
              <p className="text-text-secondary text-sm mt-1">
                Update role for {member?.fullName || 'this user'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* User Info */}
          <div className="mt-4 bg-background rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {member?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-text-primary font-medium">{member?.fullName || 'Unknown'}</p>
                <p className="text-text-secondary text-sm">{member?.email}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(member?.role)}`}>
                {formatRole(member?.role)}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} color="#ef4444" />
                <p className="text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Role Selection */}
          <div className="space-y-2">
            <label className="text-text-secondary text-sm">Select New Role</label>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableRoles.length === 0 ? (
                <p className="text-text-secondary text-sm py-4 text-center">
                  No roles available to assign
                </p>
              ) : (
                availableRoles.map(role => (
                  <button
                    key={role.value}
                    onClick={() => setSelectedRole(role.value)}
                    className={`w-full p-3 rounded-lg border transition-colors text-left ${
                      selectedRole === role.value
                        ? 'border-orange-500 bg-orange-500 bg-opacity-10'
                        : 'border-border bg-background hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-medium ${selectedRole === role.value ? 'text-orange-400' : 'text-text-primary'}`}>
                          {role.label}
                        </p>
                        <p className="text-text-secondary text-sm">{role.description}</p>
                      </div>
                      {selectedRole === role.value && (
                        <Icon name="Check" size={20} color="#FF7A00" />
                      )}
                      {member?.role === role.value && (
                        <span className="px-2 py-0.5 bg-surface-light text-text-secondary text-xs rounded">
                          Current
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Warning for role change */}
          {hasChanges && (
            <div className="mt-4 bg-yellow-500 bg-opacity-10 border border-yellow-500 border-opacity-30 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={18} color="#eab308" className="mt-0.5" />
                <p className="text-yellow-400 text-sm">
                  {selectedRole === 'contractor' && member?.role !== 'contractor'
                    ? 'Changing to contractor will reset all permissions to default contractor permissions. Any custom permission overrides will be removed.'
                    : 'Changing the role will update the user\'s default permissions.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-surface">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                hasChanges
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <span className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeRoleModal;
